/**
 * Production-safe logging utility for DCW
 * 
 * Follows canonical rules:
 * - No debug spam in production
 * - Keep critical errors and user-facing messages
 * - Maintain development experience
 */

// Buffer polyfill for test environment
// This helps with the "Named export 'Buffer' not found" error in tests
if (typeof Buffer === 'undefined' && typeof window !== 'undefined') {
  // Only define if it doesn't exist to avoid conflicts
  globalThis.Buffer = {
    isBuffer: () => false
  };
}

// Production mode check (set by Vite define)
const IS_PRODUCTION = typeof __PROD__ !== 'undefined' ? __PROD__ : false;

/**
 * Log levels for production filtering
 */
const LogLevel = {
  ERROR: 0,    // Always show: Critical errors that affect user experience
  WARN: 1,     // Show in prod: Important warnings users should see
  INFO: 2,     // Production: Only critical user information
  DEBUG: 3,    // Development only: Detailed debugging information
};

/**
 * Current log level based on environment
 */
const CURRENT_LOG_LEVEL = IS_PRODUCTION ? LogLevel.INFO : LogLevel.DEBUG;

/**
 * Safe logger that respects production environment
 */
export const logger = {
  /**
   * Critical errors - always shown
   */
  error: (...args) => {
    if (CURRENT_LOG_LEVEL >= LogLevel.ERROR) {
      if (globalThis.console && globalThis.console.error) globalThis.console.error(...args);
    }
  },

  /**
   * Important warnings - shown in production
   */
  warn: (...args) => {
    if (CURRENT_LOG_LEVEL >= LogLevel.WARN) {
      if (globalThis.console && globalThis.console.warn) globalThis.console.warn(...args);
    }
  },

  /**
   * Important information - minimal in production
   */
  info: (...args) => {
    if (CURRENT_LOG_LEVEL >= LogLevel.INFO) {
      if (globalThis.console && globalThis.console.log) {
        if (IS_PRODUCTION) {
          // Prefix informational logs in production to distinguish from warnings/errors
          globalThis.console.log('[info]', ...args);
        } else {
          globalThis.console.log(...args);
        }
      }
    }
  },

  /**
   * Debug information - development only
   */
  debug: (...args) => {
    if (CURRENT_LOG_LEVEL >= LogLevel.DEBUG) {
      if (!IS_PRODUCTION && globalThis.console && globalThis.console.log) globalThis.console.log(...args);
    }
  },

  /**
   * Authentication debug - development only
   */
  auth: (...args) => {
    if (CURRENT_LOG_LEVEL >= LogLevel.DEBUG) {
      if (!IS_PRODUCTION && globalThis.console && globalThis.console.log) globalThis.console.log('[auth]', ...args);
    }
  },

  /**
   * System initialization - show in production but clean
   */
  system: (...args) => {
    if (IS_PRODUCTION) {
      // In production, only show clean startup message
      if (args[0] && args[0].includes('DopeOS')) {
        if (globalThis.console && globalThis.console.log) globalThis.console.log('[DopeOS] initialized');
      }
    } else {
      if (globalThis.console && globalThis.console.log) globalThis.console.log('[LOG]', ...args);
    }
  },

  /**
   * Chat/API operations - minimal in production
   */
  api: (...args) => {
    if (CURRENT_LOG_LEVEL >= LogLevel.DEBUG) {
      if (!IS_PRODUCTION && globalThis.console && globalThis.console.log) globalThis.console.log('[link]', ...args);
    }
  },

  /**
   * Analytics - minimal in production
   */
  analytics: (...args) => {
    if (IS_PRODUCTION) {
      // Only show analytics errors in production
      if (args[0] && args[0].includes('Error')) {
        if (globalThis.console && globalThis.console.error) globalThis.console.error('[Analytics Error]:', ...args.slice(1));
      }
    } else {
      if (!IS_PRODUCTION && globalThis.console && globalThis.console.log) globalThis.console.log('[DEBUG]', ...args);
    }
  }
};

export default logger; 